import { Dispatch, SetStateAction, useState } from 'react';
import Dropdown from '../Dropdown/Dropdown';
import Icon from '../Icon/Icon';
import { IconKeys } from '../Icon/IconKeys';
import { RadioSearchGroup } from './RadioSearchGroup';
import S from './DropdownFilterStyles';
import CheckboxSearchOption from './CheckboxSearchOption';
import { generateFilterData, generateFilterValue } from './helpers';
import { FilterConfig } from './types';

type Props = {
  config: FilterConfig;
  filters: Record<string, string[]>;
  defaultFilters?: Record<string, string[]>;
  setFilters: Dispatch<SetStateAction<Record<string, string[]>>>;
  showValue?: boolean;
};

const areStringArraysEqual = (a: string[] = [], b: string[] = []): boolean => {
  if (a.length !== b.length) {
    return false;
  }

  const sortedA = [...a].sort();
  const sortedB = [...b].sort();
  return sortedA.every((value, index) => value === sortedB[index]);
};

function DropdownFilter({
  config,
  filters,
  defaultFilters = {},
  setFilters,
  showValue = false,
}: Props): JSX.Element {
  const { options, type, display } = config;
  const { icon, label } = generateFilterData(type);
  const [dropdownState, toggleDropdownState] = useState(false);
  const isChanged = !areStringArraysEqual(filters[type] || [], defaultFilters[type] || []);
  const filterValue = generateFilterValue(filters[type], options);

  return (
    <Dropdown
      target={(
        <S.LabelWrapper $isChanged={isChanged}>
          <Icon iconKey={icon} />
          <p title={showValue && filters[type] ? `${label}: ${filterValue}` : undefined}> {label}{showValue && filters[type] ? `: ${filterValue}` : ''} </p>
          <Icon iconKey={IconKeys.ARROW_DOWN} className="arrow-down" />
        </S.LabelWrapper>
      )}
      isOpen={dropdownState}
      onChange={toggleDropdownState}
    >
      <S.SearchList>
        {display === 'radio'
          ? (
            <RadioSearchGroup
              type={type}
              options={options}
              filters={filters}
              setFilters={setFilters}
              onClickItem={() => { toggleDropdownState(false); }}
            />
          )
          : options.map((option: Record<string, string>) => (
            <CheckboxSearchOption
              key={Object.keys(option)[0]}
              option={option}
              type={type}
              filters={filters}
              setFilters={setFilters}
            />
          ))}
      </S.SearchList>
    </Dropdown>
  );
}

export default DropdownFilter;
